import {
  useUpdateEncashmentRecordMutation,
  type EncashmentRecord,
} from '@/app/features/employee/emp-payroll';
import CustomDrawer from '@/components/CustomDrawer';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { formatNumber } from '@/utils';
import { Button, Flex, Form, Input, InputNumber } from 'antd';
import { formatBDT, toAmount } from '../shared/payroll.helpers';
import { isRecordEditable } from './encashment.helpers';

type Props = {
  open: boolean;
  batchId: string;
  record?: EncashmentRecord | null;
  onClose: () => void;
};

type FormValues = {
  /** Amount per leave type, keyed by `leave_type_id`. */
  amounts: Record<string, number | null>;
  note?: string;
};

/**
 * Edits one pending encashment record: the amount paid for each leave type and
 * a note, with the new total shown before saving.
 */
const EditEncashmentRecord = ({ open, batchId, record, onClose }: Props) => (
  <CustomDrawer
    open={open}
    onClose={onClose}
    title="Edit Encashment"
    width={550}
  >
    {/* The drawer unmounts its body when closed, so each record starts fresh. */}
    {record && (
      <RecordForm batchId={batchId} record={record} onDone={onClose} />
    )}
  </CustomDrawer>
);

const RecordForm = ({
  batchId,
  record,
  onDone,
}: {
  batchId: string;
  record: EncashmentRecord;
  onDone: () => void;
}) => {
  const [form] = Form.useForm<FormValues>();
  const [updateRecord, { isLoading: saving }] =
    useUpdateEncashmentRecordMutation();

  const breakdown = record.encashment_breakdown || [];
  const initialAmounts = Object.fromEntries(
    breakdown.map((item) => [item.leave_type_id, toAmount(item.amount)]),
  );

  const amounts = Form.useWatch('amounts', form) ?? initialAmounts;
  const total = breakdown.reduce(
    (sum, item) => sum + toAmount(amounts[item.leave_type_id]),
    0,
  );

  const handleSubmit = async (values: FormValues) => {
    // The action is hidden on other records; this also stops a forced submit.
    if (!isRecordEditable(record)) return;
    try {
      await updateRecord({
        batchId,
        recordId: record.id,
        body: {
          // Every leave line is sent back whole, with only its amount changed.
          encashment_breakdown: breakdown.map((item) => ({
            ...item,
            amount: toAmount(values.amounts?.[item.leave_type_id]),
          })),
          note: values.note?.trim() || '',
        },
      }).unwrap();
      toast('success', 'Encashment breakdown updated successfully');
      onDone();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to update encashment'));
    }
  };

  return (
    <Form
      form={form}
      size="large"
      layout="vertical"
      className="custom-form space-y-6"
      initialValues={{ amounts: initialAmounts, note: record.note || '' }}
      onFinish={handleSubmit}
    >
      <div className="rounded-xl border border-theme-gray-700 bg-theme-slate-900 p-4">
        <div className="text-white text-base font-semibold">
          {record.employee?.name || 'n/a'}
        </div>
        <div className="text-white/50 text-xs mt-0.5">
          {[record.employee?.employee_id, record.employee?.department?.name]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </div>

      {/* One line per leave type: what it is based on, and the amount */}
      <div className="space-y-4">
        {breakdown.map((item) => (
          <Flex
            key={item.leave_type_id}
            align="center"
            justify="space-between"
            gap={16}
          >
            <div className="min-w-0">
              <div className="text-white text-sm font-medium truncate">
                {item.leave_name}
              </div>
              <div className="text-white/40 text-xs">
                {item.days} days × {formatNumber(toAmount(item.per_day_rate))}
                {item.percentage ? ` · ${item.percentage}%` : ''}
              </div>
            </div>
            <Form.Item
              name={['amounts', item.leave_type_id]}
              rules={[{ required: true, message: 'Please enter amount' }]}
              className="!mb-0 w-[180px] shrink-0"
            >
              <InputNumber
                min={0}
                precision={2}
                controls={false}
                prefix="৳"
                className="w-full"
              />
            </Form.Item>
          </Flex>
        ))}
      </div>

      <Form.Item name="note" label="Note">
        <Input.TextArea rows={2} maxLength={250} placeholder="Optional" />
      </Form.Item>

      <Flex
        justify="space-between"
        align="center"
        className="rounded-xl border border-theme-primary/30 bg-theme-primary/10 p-4"
      >
        <span className="text-white/60 text-xs uppercase tracking-wider">
          Total
        </span>
        <span className="text-theme-primary text-2xl font-bold">
          {formatBDT(total)}
        </span>
      </Flex>

      <Flex justify="end" gap={12}>
        <Button
          className="btn-primary-bordered min-w-[80px]"
          onClick={onDone}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          htmlType="submit"
          loading={saving}
          className="btn-primary min-w-[80px] h-11 font-semibold"
        >
          Save Changes
        </Button>
      </Flex>
    </Form>
  );
};

export default EditEncashmentRecord;
